import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Formik, Form as FormikForm } from 'formik'
import * as Yup from 'yup'
import { api } from '../../../../../auth'
import { useAppSelector } from '../../../../../store/hooks'
import { useToast } from '../../../../common/toast/hooks/useToast'
import { handleAxiosErrors } from '../../../../common/forms/errors/Helper'
import {
    getLocalStorageValue,
    setSettingsValue,
    getGroupTranslation,
} from '../../../../common/utility/helpers'
import { Language } from '../../../../../features/security/security.slice'
import Common from '../../../../common/Common'
import RenderFormikErrors from '../../../../common/forms/errors/RenderFormikErrors'
import EditorToolbar from './EditorToolbar'
import BuilderCanvas from './BuilderCanvas'
import PropertiesPanel from './PropertiesPanel'
import PageSectionForm from '../../page-sections/PageSectionForm'
import {
    BuilderEditorProps,
    BuilderPage,
    BuilderGridItem,
    PreviewTheme,
    DevicePreview,
    Choice,
} from '../types'
import { Alert, Button } from 'react-bootstrap'
import { Dialog } from 'primereact/dialog'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleNotch, faQuestionCircle } from '@fortawesome/free-solid-svg-icons'

const PageBuilderEditor: FC<BuilderEditorProps> = ({ pageId }) => {
    const navigate = useNavigate()
    const axiosPrivate = api
    const theme = useAppSelector((state) => state.theme.theme)
    const initData = useAppSelector((state: any) => state.security.initData)
    const { triggerToast } = useToast()
    const formRef = useRef<HTMLFormElement>(null)
    const formikRef = useRef<any>(null)

    const emptyPage: BuilderPage = {
        page_title: '',
        code: '',
        name: '',
        header_size_id: null,
        meta_description: '',
        meta_keywords: '',
        main_header_top: '',
        main_header_middle_big: '',
        main_header_bottom: '',
        header_image_id: null,
        header_image: '',
        remove_header_image: false,
        is_template: false,
        ctas: {},
        pillars: {},
    }

    const pageCode = 'PAGE_' + pageId
    const [page, setPage] = useState<BuilderPage>(emptyPage)
    const [sections, setSections] = useState<BuilderGridItem[]>([])
    const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null)
    const [availablePages, setAvailablePages] = useState<Choice[]>([])
    const [headerSizes, setHeaderSizes] = useState<Choice[]>([])
    const [headerSizeCodes, setHeaderSizeCodes] = useState<Record<string, string>>({})
    const [deletedCtas, setDeletedCtas] = useState<string[]>([])

    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [isDirty, setIsDirty] = useState(false)
    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<Record<string, string>>({})

    const [allowRemoveImage, setAllowRemoveImage] = useState(false)
    const [previewSrc, setPreviewSrc] = useState('')

    const settings = getLocalStorageValue('settings', {})
    const [originalPreviewTheme, setOriginalPreviewTheme] = useState<PreviewTheme | null>(null)
    const [previewTheme, setPreviewTheme] = useState<PreviewTheme | null>(null)

    const defaultLocale = initData.languages.default?.locale || Object.keys(initData.languages.data)[0]
    const [activeLocale, setActiveLocale] = useState<string>(
        (settings.builderLocale as string) || defaultLocale
    )
    const [devicePreview, setDevicePreview] = useState<DevicePreview>(
        (settings.builderDevicePreview as DevicePreview) || 'desktop'
    )
    const [rightPanelOpen, setRightPanelOpen] = useState(
        settings.builderPanelOpen !== false
    )
    const [showSectionForm, setShowSectionForm] = useState(false)
    const [editingSectionId, setEditingSectionId] = useState<number | null>(null)
    const [sectionToRemove, setSectionToRemove] = useState<string | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const [translations, setTranslations] = useState<Record<string, unknown>>({})
    const [showDiscardDialog, setShowDiscardDialog] = useState(false)
    const isDirtyRef = useRef(false)
    const [originalSnapshot, setOriginalSnapshot] = useState<string>('')
    const [modifiedSections, setModifiedSections] = useState<Set<string>>(new Set())

    const createSnapshot = (items: BuilderGridItem[]) => {
        return JSON.stringify(
            items.map((s) => {
                const p = s.pagePart as { page_content_active?: boolean | number; active?: boolean | number }
                const raw = p.page_content_active ?? p.active
                return {
                    id: s.id,
                    active: raw === true || raw === 1,
                }
            })
        )
    }

    const checkDirty = (currentSections: BuilderGridItem[]) => {
        const currentSnap = createSnapshot(currentSections)
        return currentSnap !== originalSnapshot
    }

    useEffect(() => {
        isDirtyRef.current = isDirty
    }, [isDirty])

    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (isDirtyRef.current) {
                e.preventDefault()
            }
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [])

    const _initialValues: Record<string, unknown> = {
        ...page,
        value: pageCode + '_VALUE',
        main_header_top: pageCode + '_MAIN_HEADER_TOP',
        main_header_middle_big: pageCode + '_MAIN_HEADER_MIDDLE_BIG',
        main_header_bottom: pageCode + '_MAIN_HEADER_BOTTOM',
    }
    const [initialValues, setInitialValues] = useState(_initialValues)

    const PageFormSchema = Yup.object().shape({
        code: Yup.string().trim().required('Enter Page Url'),
        name: Yup.string().trim().required('Enter Page Internal Name'),
    })

    const loadPage = useCallback(async () => {
        setErrMsg('')
        setErrorFields({})
        setIsLoading(true)

        try {
            const response = await axiosPrivate
                .get('/page-components/builder/page/' + pageId)
                .finally(() => setIsLoading(false))

            if (response.data?.success === true) {
                const data = response.data.data

                setOriginalPreviewTheme({
                    colors: data.theme.colors,
                })

                if (data.page) {
                    setPage(data.page)
                    const filteredSections = (data.grid_items || []).filter(
                        (item: BuilderGridItem) =>
                            item.type?.code !== 'header'
                    )
                    setSections(filteredSections)
                    setOriginalSnapshot(createSnapshot(filteredSections))

                    setInitialValues((prevState) => ({
                        ...prevState,
                        ...data.page,
                        main_header_top: data.page_headers?.main_header_top || prevState.main_header_top,
                        main_header_middle_big: data.page_headers?.main_header_middle_big || prevState.main_header_middle_big,
                        main_header_bottom: data.page_headers?.main_header_bottom || prevState.main_header_bottom,
                    }))

                    setTranslations(data.translations || {})

                    Object.keys(initData.languages.data).map((locale) => {
                        setPage((prevState) => {
                            const lang: Language =
                                initData.languages.data[locale]

                            const groupTranslations: Record<string, any> =
                                getGroupTranslation(
                                    data.translations,
                                    [
                                        {
                                            key: 'main_header_top',
                                            code: data.page_headers
                                                .main_header_top,
                                        },
                                        {
                                            key: 'main_header_middle_big',
                                            code: data.page_headers
                                                .main_header_middle_big,
                                        },
                                        {
                                            key: 'main_header_bottom',
                                            code: data.page_headers
                                                .main_header_bottom,
                                        },
                                    ],
                                    locale
                                )

                            setInitialValues((prevState) => ({
                                ...prevState,
                                [`main_header_top_${lang.id}`]:
                                    groupTranslations['main_header_top'] ?? '',
                                [`main_header_middle_big_${lang.id}`]:
                                    groupTranslations[
                                        'main_header_middle_big'
                                    ] ?? '',
                                [`main_header_bottom_${lang.id}`]:
                                    groupTranslations['main_header_bottom'] ?? '',
                            }))

                            return {
                                ...prevState,
                                [`main_header_top_${lang.id}`]:
                                    groupTranslations['main_header_top'] ?? '',
                                [`main_header_middle_big_${lang.id}`]:
                                    groupTranslations[
                                        'main_header_middle_big'
                                    ] ?? '',
                                [`main_header_bottom_${lang.id}`]:
                                    groupTranslations['main_header_bottom'] ?? '',
                            }
                        })

                        return true
                    })

                    setPage((prev) => ({
                        ...prev,
                        pillars: data.pillars || {},
                    }))

                    setAllowRemoveImage(data.page.header_image_id !== null)
                    setPreviewSrc(
                        data.page.header_image_id
                            ? `${import.meta.env.VITE_API_ROOT_URL}/media/download/${data.page.header_image_id}`
                            : ''
                    )
                }

                setAvailablePages(data.available_pages || [])
                setHeaderSizes(data.header_sizes || [])
                setHeaderSizeCodes(data.header_size_codes || {})

                setIsDirty(false)
                setModifiedSections(new Set())
            } else {
                setErrMsg(response.data?.errors?.join(', ') || 'Failed to load page')
            }
        } catch (err: unknown) {
            const error = err as { response?: { status?: number } }
            if (!error?.response) {
                setErrMsg('No Server Response')
            } else if (error.response?.status === 404) {
                setErrMsg('Page was not found')
            } else {
                setErrMsg('Failed to load page')
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [axiosPrivate, pageId, initData.languages.data])

    useEffect(() => {
        loadPage()
    }, [loadPage])

    useEffect(() => {
        if (previewTheme === null) {
            setPreviewTheme(
                (settings.previewTheme as PreviewTheme) || originalPreviewTheme
            )
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [originalPreviewTheme, previewTheme])

    const saveModifiedSections = async () => {
        if (modifiedSections.size === 0) return

        const languages = initData.languages.data as Record<string, { id: string | number; locale?: string }>

        for (const sectionId of modifiedSections) {
            const section = sections.find((s) => s.id === sectionId)
            if (!section) continue

            const pillar = section.pagePart as {
                page_content_id?: number
                page_content_name?: string
                page_content_value?: string | Record<string, string>
                page_content_active?: boolean | number
                active?: boolean | number
                page_sections_id?: number
            }
            if (!pillar.page_content_id) continue

            const contentId = pillar.page_content_id
            const translationCode = `PAGE_SECTION_${contentId}_VALUE`
            const valueObj = pillar.page_content_value

            const activeFlag = pillar.page_content_active ?? pillar.active
            const translations: Record<string, Record<string, string>> = {}

            if (valueObj && typeof valueObj === 'object') {
                for (const locale of Object.keys(languages)) {
                    const langId = languages[locale].id
                    const content = valueObj['_' + langId]
                    if (content !== undefined) {
                        translations[locale] = { [translationCode]: content }
                    }
                }
            }

            const sectionPayload: Record<string, unknown> = {
                name: pillar.page_content_name || '',
                active: (activeFlag === 1 || activeFlag === true) ? 'true' : 'false',
                translations,
            }

            const imageFile = formikRef.current?.values[`pillar_image_${contentId}`]
            if (imageFile instanceof File) {
                sectionPayload.pillar_image = imageFile
            }

            try {
                await axiosPrivate.post(
                    `/page-components/sections/inline-update/${contentId}`,
                    sectionPayload,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                )
            } catch {
                triggerToast({
                    severity: 'error',
                    summary: 'Error',
                    detail: `Failed to save section "${pillar.page_content_name}"`,
                })
            }
        }
    }

    const handleSave = async (record: Record<string, unknown>) => {
        setErrMsg('')
        setIsSaving(true)

        const languages = initData.languages.data as Record<string, { id: string | number }>

        const submitData: Record<string, unknown> = {
            page_title: record.page_title,
            name: record.name,
            code: record.code,
            header_size_id: record.header_size_id,
            meta_description: record.meta_description,
            meta_keywords: record.meta_keywords,
            is_template: record.is_template,
            main_header_top: record.main_header_top,
            main_header_middle_big: record.main_header_middle_big,
            main_header_bottom: record.main_header_bottom,
            ctas: record.ctas,
            deletedCtas,
            remove_header_image:
                !record.header_image &&
                record.header_image_id !== null &&
                !allowRemoveImage,
        }

        if (record.header_image instanceof File) {
            submitData.header_image = record.header_image
        }

        for (const locale of Object.keys(languages)) {
            const langId = languages[locale].id
            for (const field of ['main_header_top', 'main_header_middle_big', 'main_header_bottom']) {
                const key = `${field}_${langId}`
                submitData[key] = (record[key] as string) ?? ''
            }
        }

        try {
            const response = await axiosPrivate
                .post('/page-components/builder/page/' + pageId, submitData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })

            if (response.data?.success === true) {
                await saveModifiedSections()
                await handleSaveLayout(sections)

                triggerToast({
                    severity: 'success',
                    summary: 'Saved',
                    detail: response.data.data.msg || 'Page updated successfully',
                })
                setModifiedSections(new Set())
                setDeletedCtas([])
                await loadPage()
                setIsDirty(false)
            } else {
                setErrMsg(response.data?.errors?.join(', ') || 'Save failed')
            }
        } catch (err: unknown) {
            handleAxiosErrors(err, setErrMsg, setErrorFields as any)
        } finally {
            setIsSaving(false)
        }
    }

    const handleSaveLayout = async (reorderedSections: BuilderGridItem[]) => {
        const sectionsOrder: Record<string, number> = {}
        reorderedSections.forEach((section, index) => {
            const contentId = (section.pagePart as Record<string, unknown>)?.page_content_id
            if (contentId) {
                sectionsOrder[String(contentId)] = index + 1
            }
        })

        try {
            await axiosPrivate.post(
                'page-components/sections/layout/update',
                { sectionsOrder },
                { headers: { 'Content-Type': 'multipart/form-data' } }
            )
        } catch {
            // Layout save is best-effort during reorder
        }
    }

    const handleReorder = (reorderedSections: BuilderGridItem[]) => {
        setSections(reorderedSections)
        setIsDirty(checkDirty(reorderedSections))
    }

    const handleRemoveSectionRequest = (sectionId: string) => {
        setSectionToRemove(sectionId)
    }

    const handleConfirmRemoveSection = async () => {
        if (!sectionToRemove) return

        const section = sections.find((s) => s.id === sectionToRemove)
        if (!section?.pagePart) {
            setSectionToRemove(null)
            return
        }

        const pillar = section.pagePart as { page_content_id?: number }
        if (!pillar.page_content_id) {
            setSectionToRemove(null)
            return
        }

        setIsDeleting(true)
        try {
            const response = await axiosPrivate.post(
                `/page-components/sections/delete/${pillar.page_content_id}`
            )
            if (response.data?.success === true) {
                setSections((prev) => prev.filter((s) => s.id !== sectionToRemove))
                if (selectedSectionId === sectionToRemove) {
                    setSelectedSectionId(null)
                }
                triggerToast({
                    severity: 'success',
                    summary: 'Removed',
                    detail: 'Section removed successfully',
                })
            }
        } catch {
            triggerToast({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to remove section',
            })
        } finally {
            setIsDeleting(false)
            setSectionToRemove(null)
        }
    }

    const sectionToRemoveName = sectionToRemove
        ? (sections.find((s) => s.id === sectionToRemove)?.pagePart as {
              page_content_name?: string
          })?.page_content_name || 'this section'
        : ''

    const handleToggleActive = (sectionId: string) => {
        setSections((prev) => {
            const updated = prev.map((s) => {
                if (s.id === sectionId) {
                    const pillar = s.pagePart as { page_content_active?: boolean | number; active?: boolean | number; [key: string]: unknown }
                    const activeFlag = pillar.page_content_active ?? pillar.active
                    const currentActive = activeFlag !== false && activeFlag !== 0
                    return {
                        ...s,
                        pagePart: {
                            ...s.pagePart,
                            page_content_active: currentActive ? 0 : 1,
                            active: currentActive ? 0 : 1,
                        },
                    }
                }
                return s
            })
            setIsDirty(checkDirty(updated))
            return updated
        })
        setModifiedSections((prev) => new Set(prev).add(sectionId))
    }

    const handleContentChange = (sectionId: string, locale: string, content: string) => {
        const langId = initData.languages.data[locale]?.id
        if (!langId) return

        setSections((prev) =>
            prev.map((s) => {
                if (s.id === sectionId) {
                    const pillar = s.pagePart as {
                        page_content_value?: string | Record<string, string>
                        [key: string]: unknown
                    }
                    const currentValue = typeof pillar.page_content_value === 'object'
                        ? pillar.page_content_value
                        : {}
                    return {
                        ...s,
                        pagePart: {
                            ...s.pagePart,
                            page_content_value: {
                                ...currentValue,
                                ['_' + langId]: content,
                            },
                        },
                    }
                }
                return s
            })
        )
        setModifiedSections((prev) => new Set(prev).add(sectionId))
        setIsDirty(true)
    }

    const handleImageChange = (sectionId: string, file: File) => {
        const section = sections.find((s) => s.id === sectionId)
        if (!section) return
        const pillar = section.pagePart as { page_content_id?: number }
        if (pillar.page_content_id) {
            const previewUrl = URL.createObjectURL(file)
            setSections((prev) =>
                prev.map((s) =>
                    s.id === sectionId
                        ? { ...s, pagePart: { ...s.pagePart, image_url: previewUrl, img_url: previewUrl } }
                        : s
                )
            )
            formikRef.current?.setFieldValue(
                `pillar_image_${pillar.page_content_id}`,
                file
            )
            setModifiedSections((prev) => new Set(prev).add(sectionId))
            setIsDirty(true)
        }
    }

    const handleNameChange = (sectionId: string, name: string) => {
        setSections((prev) =>
            prev.map((s) =>
                s.id === sectionId
                    ? { ...s, pagePart: { ...s.pagePart, page_content_name: name } }
                    : s
            )
        )
        setModifiedSections((prev) => new Set(prev).add(sectionId))
        setIsDirty(true)
    }

    const [showResetDialog, setShowResetDialog] = useState(false)

    const handleReset = () => {
        if (isDirty) setShowResetDialog(true)
    }

    const handleConfirmReset = async () => {
        setShowResetDialog(false)
        setPreviewTheme(originalPreviewTheme)
        await loadPage()
        setIsDirty(false)
    }

    const handleAddSection = () => {
        setEditingSectionId(null)
        setShowSectionForm(true)
    }

    const handleEditSection = (sectionId: number) => {
        setEditingSectionId(sectionId)
        setShowSectionForm(true)
    }

    const handleSectionFormClose = () => {
        setShowSectionForm(false)
        setEditingSectionId(null)
        loadPage()
    }

    const selectedSection =
        sections.find((s) => s.id === selectedSectionId) || null

    return (
        <div className="sfy-page-builder-editor">
            {isLoading && <Common.Loader text="Loading builder..." />}

            {errMsg && (
                <Alert variant="danger" className="m-3" dismissible onClose={() => setErrMsg('')}>
                    {errMsg}
                </Alert>
            )}

            <Formik
                initialValues={initialValues}
                validationSchema={PageFormSchema}
                onSubmit={(values) => handleSave(values as Record<string, unknown>)}
                enableReinitialize={true}
            >
                {(formikProps) => {
                    formikRef.current = formikProps
                    return (
                    <FormikForm
                        id="page-builder-form"
                        ref={formRef}
                        className="sfy-page-builder-editor__form"
                    >
                        <RenderFormikErrors formikProps={formikProps} />

                        <EditorToolbar
                            pageName={page.name || 'Untitled Page'}
                            isSaving={isSaving}
                            isDirty={isDirty}
                            devicePreview={devicePreview}
                            onDeviceChange={(device) => {
                                setDevicePreview(device)
                                setSettingsValue('builderDevicePreview', device)
                            }}
                            onSave={() => {
                                const submitBtn = document.getElementById(
                                    'page-builder-submit-btn'
                                )
                                submitBtn?.click()
                            }}
                            onBack={() => {
                                if (isDirty) {
                                    setShowDiscardDialog(true)
                                } else {
                                    navigate('/content/page-builder')
                                }
                            }}
                            rightPanelOpen={rightPanelOpen}
                            onToggleRightPanel={() => {
                                setRightPanelOpen((p) => {
                                    setSettingsValue('builderPanelOpen', !p)
                                    return !p
                                })
                            }}
                            activeLocale={activeLocale}
                            onLocaleChange={(locale) => {
                                setActiveLocale(locale)
                                setSettingsValue('builderLocale', locale)
                            }}
                            onAddSection={handleAddSection}
                            onReset={handleReset}
                        />

                        <div className="sfy-page-builder-editor__workspace">
                            <div
                                className={`sfy-page-builder-editor__canvas-area ${
                                    rightPanelOpen
                                        ? 'sfy-page-builder-editor__canvas-area--with-panel'
                                        : ''
                                }`}
                            >
                                <BuilderCanvas
                                    sections={sections}
                                    selectedSectionId={selectedSectionId}
                                    onSelectSection={setSelectedSectionId}
                                    onReorder={handleReorder}
                                    onEditSection={handleEditSection}
                                    onRemoveSection={handleRemoveSectionRequest}
                                    onToggleActive={handleToggleActive}
                                    onContentChange={handleContentChange}
                                    onImageChange={handleImageChange}
                                    onNameChange={handleNameChange}
                                    devicePreview={devicePreview}
                                    page={page}
                                    previewTheme={previewTheme}
                                    formikProps={formikProps}
                                    availablePages={availablePages}
                                    deletedCtas={deletedCtas}
                                    setDeletedCtas={setDeletedCtas}
                                    headerSizeCodes={headerSizeCodes}
                                    translations={translations}
                                    activeLocale={activeLocale}
                                />
                            </div>

                            <PropertiesPanel
                                isOpen={rightPanelOpen}
                                selectedSection={selectedSection}
                                page={page}
                                formikProps={formikProps}
                                availablePages={availablePages}
                                headerSizes={headerSizes}
                                previewTheme={previewTheme}
                                setPreviewTheme={setPreviewTheme}
                                previewSrc={previewSrc}
                                setPreviewSrc={setPreviewSrc}
                                allowRemoveImage={allowRemoveImage}
                                setAllowRemoveImage={setAllowRemoveImage}
                                setPage={setPage}
                                onDeselectSection={() =>
                                    setSelectedSectionId(null)
                                }
                                onAddSection={handleAddSection}
                                onEditSection={handleEditSection}
                            />
                        </div>

                        <input
                            type="submit"
                            id="page-builder-submit-btn"
                            className="d-none"
                        />
                    </FormikForm>
                    )
                }}
            </Formik>

            {showSectionForm && (
                <PageSectionForm
                    inPopup={true}
                    id={editingSectionId}
                    onClose={handleSectionFormClose}
                />
            )}

            <Dialog
                header={
                    <span>
                        <FontAwesomeIcon icon={faQuestionCircle} /> Confirm
                        Removal
                    </span>
                }
                visible={sectionToRemove !== null}
                breakpoints={{ '960px': '60vw', '640px': '80vw' }}
                style={{ width: '35vw' }}
                onHide={() => setSectionToRemove(null)}
                closable={!isDeleting}
                footer={
                    <div>
                        <Button
                            variant={theme === 'dark' ? 'dark' : 'light'}
                            onClick={() => setSectionToRemove(null)}
                            disabled={isDeleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleConfirmRemoveSection}
                            disabled={isDeleting}
                        >
                            <span className="text-white">
                                {isDeleting ? (
                                    <>
                                        <FontAwesomeIcon
                                            icon={faCircleNotch}
                                            spin
                                        />{' '}
                                        Removing...
                                    </>
                                ) : (
                                    'Yes, Remove'
                                )}
                            </span>
                        </Button>
                    </div>
                }
            >
                <p className="mb-3 pb-3 border-bottom">
                    Are you sure you want to remove section{' '}
                    <strong>{sectionToRemoveName}</strong> from this page?
                </p>
                <p className="text-muted small mb-0">
                    This action cannot be undone.
                </p>
            </Dialog>

            <Dialog
                header={
                    <span>
                        <FontAwesomeIcon icon={faQuestionCircle} /> Reset
                        Changes
                    </span>
                }
                visible={showResetDialog}
                breakpoints={{ '960px': '60vw', '640px': '80vw' }}
                style={{ width: '35vw' }}
                onHide={() => setShowResetDialog(false)}
                closable
                footer={
                    <div>
                        <Button
                            variant={theme === 'dark' ? 'dark' : 'light'}
                            onClick={() => setShowResetDialog(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleConfirmReset}
                        >
                            <span className="text-white">Yes, Reset</span>
                        </Button>
                    </div>
                }
            >
                <p className="mb-3 pb-3 border-bottom">
                    Are you sure you want to reset all unsaved changes?
                </p>
                <p className="text-muted small mb-0">
                    This will reload the page data from the server and discard
                    all local modifications.
                </p>
            </Dialog>

            <Dialog
                header={
                    <span>
                        <FontAwesomeIcon icon={faQuestionCircle} /> Unsaved
                        Changes
                    </span>
                }
                visible={showDiscardDialog}
                breakpoints={{ '960px': '60vw', '640px': '80vw' }}
                style={{ width: '35vw' }}
                onHide={() => setShowDiscardDialog(false)}
                closable
                footer={
                    <div>
                        <Button
                            variant={theme === 'dark' ? 'dark' : 'light'}
                            onClick={() => setShowDiscardDialog(false)}
                        >
                            Stay
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => {
                                setShowDiscardDialog(false)
                                setIsDirty(false)
                                navigate('/content/page-builder')
                            }}
                        >
                            <span className="text-white">Discard Changes</span>
                        </Button>
                    </div>
                }
            >
                <p className="mb-3 pb-3 border-bottom">
                    You have unsaved changes. Are you sure you want to leave
                    this page?
                </p>
                <p className="text-muted small mb-0">
                    Your changes will be lost if you navigate away.
                </p>
            </Dialog>
        </div>
    )
}

export default PageBuilderEditor
